/**
 * Persistent player analytics counters (localStorage).
 * Kept separate from gameplay progress so lifetime stats survive Play Again.
 */

import {
  PLAYER_STATS_STORAGE_KEY,
  StorageSlot,
  readStorageRecord,
  writeStorageRecord,
} from "./storage.js";
import { getAppVersion } from "../config/app-version.js";

export { PLAYER_STATS_STORAGE_KEY };

export function getLocalDayKey(date) {
  var d = date instanceof Date ? date : new Date(date);
  var y = d.getFullYear();
  var m = String(d.getMonth() + 1).padStart(2, "0");
  var day = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

/** @param {Record<string, string | undefined> | null | undefined} [env] */
export function resolveBuildVariantFromEnv(env) {
  var e = env || (typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {});
  var explicit = e.VITE_BUILD_VARIANT != null ? String(e.VITE_BUILD_VARIANT).trim() : "";
  if (explicit) return explicit;
  return "yespuzzles";
}

export function getBuildVariant() {
  return resolveBuildVariantFromEnv();
}

/** FRVR storefront flavours (`frvr`, `frvr-test`, `frvr_frvr`, `frvr-xiaomi`, …). */
export function isFrvrBuildVariant(variant) {
  var value = variant != null ? String(variant).trim() : getBuildVariant();
  return value === "frvr" || value.indexOf("frvr-") === 0 || value.indexOf("frvr_") === 0;
}

function normalizeNonNegInt(value, fallback) {
  var n = Math.floor(Number(value));
  if (!isFinite(n) || n < 0) return fallback;
  return n;
}

function clampVictoryFeedbackValue(value, fallback) {
  var n = Math.floor(Number(value));
  if (!isFinite(n) || n < 0) return fallback;
  return Math.max(0, Math.min(10, n));
}

export function createDefaultPlayerStats() {
  return {
    runIndex: 0,
    sessionIndex: 0,
    dayIndex: 0,
    maxTierEver: 0,
    totalPlayTimeMs: 0,
    buildVariant: getBuildVariant(),
    lastLossReason: null,
    scoreAtLoss: 0,
    bestScore: 0,
    lastLaunchDayKey: null,
    hasLaunchedBefore: false,
    pendingNextRunIndexBump: false,
    victoryFeedbackSubmitted: false,
    victoryFeedbackValue: 0,
  };
}

export function normalizePlayerStats(raw) {
  var defaults = createDefaultPlayerStats();
  if (!raw || typeof raw !== "object") return defaults;
  return {
    runIndex: normalizeNonNegInt(raw.runIndex, defaults.runIndex),
    sessionIndex: normalizeNonNegInt(raw.sessionIndex, defaults.sessionIndex),
    dayIndex: normalizeNonNegInt(raw.dayIndex, defaults.dayIndex),
    maxTierEver: normalizeNonNegInt(raw.maxTierEver, defaults.maxTierEver),
    totalPlayTimeMs: normalizeNonNegInt(raw.totalPlayTimeMs, defaults.totalPlayTimeMs),
    buildVariant: typeof raw.buildVariant === "string" ? raw.buildVariant : defaults.buildVariant,
    lastLossReason: typeof raw.lastLossReason === "string" ? raw.lastLossReason : null,
    scoreAtLoss: normalizeNonNegInt(raw.scoreAtLoss, defaults.scoreAtLoss),
    bestScore: normalizeNonNegInt(raw.bestScore, defaults.bestScore),
    lastLaunchDayKey: typeof raw.lastLaunchDayKey === "string" ? raw.lastLaunchDayKey : null,
    hasLaunchedBefore: !!raw.hasLaunchedBefore,
    pendingNextRunIndexBump: !!raw.pendingNextRunIndexBump,
    victoryFeedbackSubmitted: !!raw.victoryFeedbackSubmitted,
    victoryFeedbackValue: clampVictoryFeedbackValue(
      raw.victoryFeedbackValue,
      defaults.victoryFeedbackValue
    ),
  };
}

export function loadPlayerStats(storage) {
  storage = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!storage) return createDefaultPlayerStats();
  var raw = readStorageRecord(storage, StorageSlot.PLAYER_STATS);
  if (!raw) return createDefaultPlayerStats();
  return normalizePlayerStats(raw);
}

export function savePlayerStats(stats, storage) {
  storage = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!storage || !stats) return;
  writeStorageRecord(storage, StorageSlot.PLAYER_STATS, normalizePlayerStats(stats));
}

/**
 * Call once per page load. First launch keeps session/day at 0.
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 * @param {Date} [now]
 */
export function recordSessionStart(stats, now) {
  stats = normalizePlayerStats(stats);
  var todayKey = getLocalDayKey(now || new Date());

  if (stats.hasLaunchedBefore) {
    stats.sessionIndex += 1;
    if (stats.lastLaunchDayKey !== todayKey) {
      stats.dayIndex += 1;
    }
  } else {
    stats.hasLaunchedBefore = true;
  }

  stats.lastLaunchDayKey = todayKey;
  stats.buildVariant = getBuildVariant();
  return stats;
}

/**
 * Increment after a terminal game-over loss (flask shattered or overflow).
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 * @param {{ reason?: string, scoreAtLoss?: number, bestScore?: number }} [loss]
 */
export function recordGameLoss(stats, loss) {
  stats = normalizePlayerStats(stats);
  if (loss && typeof loss === "object") {
    if (loss.reason) stats.lastLossReason = String(loss.reason);
    if (loss.scoreAtLoss != null) {
      stats.scoreAtLoss = Math.max(0, Math.floor(Number(loss.scoreAtLoss) || 0));
    }
    if (loss.bestScore != null) {
      stats.bestScore = Math.max(stats.bestScore, Math.max(0, Math.floor(Number(loss.bestScore) || 0)));
    }
  }
  stats.pendingNextRunIndexBump = true;
  return stats;
}

/**
 * Arm run_index bump for the next fresh run (e.g. revive offer shown — abandoning counts as ending the run).
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 */
export function markPendingRunIndexBump(stats) {
  stats = normalizePlayerStats(stats);
  stats.pendingNextRunIndexBump = true;
  return stats;
}

/**
 * Run continues (e.g. revive succeeded) — do not bump run_index on the next start.
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 */
export function clearPendingRunIndexBump(stats) {
  stats = normalizePlayerStats(stats);
  stats.pendingNextRunIndexBump = false;
  return stats;
}

/**
 * Increment lifetime run counter when a new run begins (Play Again or boot after loss/revive abandon).
 * No-op when no bump is pending (e.g. first launch, checkpoint resume, mid-run refresh).
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 */
export function recordRunStart(stats) {
  stats = normalizePlayerStats(stats);
  if (!stats.pendingNextRunIndexBump) return stats;
  stats.runIndex += 1;
  stats.pendingNextRunIndexBump = false;
  return stats;
}

/**
 * Bump lifetime max tier and return each newly crossed tier in order (no skips).
 * @returns {{ stats: ReturnType<typeof createDefaultPlayerStats>, newTiers: number[] }}
 */
export function recordMaxTierEver(stats, tier) {
  stats = normalizePlayerStats(stats);
  var previousMax = stats.maxTierEver;
  var t = Math.max(0, Math.floor(Number(tier) || 0));
  if (t > stats.maxTierEver) stats.maxTierEver = t;
  var newTiers = [];
  for (var i = previousMax + 1; i <= stats.maxTierEver; i++) {
    newTiers.push(i);
  }
  return { stats: stats, newTiers: newTiers };
}

export function addPlayTimeMs(stats, deltaMs) {
  stats = normalizePlayerStats(stats);
  var delta = Math.max(0, Math.floor(Number(deltaMs) || 0));
  if (delta <= 0) return stats;
  stats.totalPlayTimeMs += delta;
  return stats;
}

export function syncBestScore(stats, bestScore) {
  stats = normalizePlayerStats(stats);
  var best = Math.max(0, Math.floor(Number(bestScore) || 0));
  if (best > stats.bestScore) stats.bestScore = best;
  return stats;
}

/**
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 * @param {number} value 0–10 content desire
 */
export function recordVictoryFeedback(stats, value) {
  stats = normalizePlayerStats(stats);
  stats.victoryFeedbackSubmitted = true;
  stats.victoryFeedbackValue = clampVictoryFeedbackValue(value, 0);
  return stats;
}

/**
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 */
export function clearVictoryFeedback(stats) {
  stats = normalizePlayerStats(stats);
  stats.victoryFeedbackSubmitted = false;
  stats.victoryFeedbackValue = 0;
  return stats;
}

/**
 * @param {ReturnType<typeof createDefaultPlayerStats>} stats
 * @param {{
 *   currentGameMaxTier?: number,
 *   cycleIndex?: number,
 *   stage?: string,
 *   score?: number,
 *   hp?: number,
 *   gems?: number,
 * }} [runtime]
 */
export function toPlayerStatsSnapshot(stats, runtime) {
  runtime = runtime || {};
  stats = normalizePlayerStats(stats);
  var out = {
    run_index: stats.runIndex,
    session_index: stats.sessionIndex,
    day_index: stats.dayIndex,
    current_tier: Math.max(0, Math.floor(Number(runtime.currentGameMaxTier) || 0)),
    max_tier_ever: stats.maxTierEver,
    cycle_index: Math.max(0, Math.floor(Number(runtime.cycleIndex) || 0)),
    last_loss_reason: stats.lastLossReason,
    total_play_time_ms: stats.totalPlayTimeMs,
    build_variant: stats.buildVariant || getBuildVariant(),
    app_version: getAppVersion(),
    score_at_loss: stats.scoreAtLoss,
    best_score: stats.bestScore,
    score: Math.max(0, Math.floor(Number(runtime.score) || 0)),
    hp: Math.max(0, Math.floor(Number(runtime.hp) || 0)),
    gems: Math.max(0, Math.floor(Number(runtime.gems) || 0)),
  };
  if (runtime.stage === "fall" || runtime.stage === "merge") {
    out.stage = runtime.stage;
  }
  return out;
}
